import * as React from 'react';
import { Box, createToaster, Toast, Toaster } from '@chakra-ui/react';
import type { Options as ToastOptions } from '@zag-js/toast';

export const toaster = createToaster({ placement: 'top-end' });

/** Toasts created with `position: 'center'` / `middle` render here (viewport center). */
export const centeredToaster = createToaster({ placement: 'top' });

type LegacyStatus =
  | 'info'
  | 'warning'
  | 'success'
  | 'error'
  | 'loading'
  | string
  | undefined;

export type LegacyToastOptions = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  status?: LegacyStatus;
  duration?: number;
  position?: string;
  isClosable?: boolean;
  id?: string;
};

const positionMap: Record<
  string,
  'top-start' | 'top' | 'top-end' | 'bottom-start' | 'bottom' | 'bottom-end'
> = {
  'top-right': 'top-end',
  'top-end': 'top-end',
  'top-left': 'top-start',
  'top-start': 'top-start',
  'bottom-right': 'bottom-end',
  'bottom-end': 'bottom-end',
  'bottom-left': 'bottom-start',
  'bottom-start': 'bottom-start',
  bottom: 'bottom',
  top: 'top',
};

function mapStatusToType(status: LegacyStatus): string {
  if (!status) {
    return 'info';
  }
  if (status === 'warning') {
    return 'warning';
  }
  return status as 'success' | 'error' | 'loading' | 'info';
}

function isCenterPosition(position: string | undefined): boolean {
  if (!position) {
    return false;
  }
  const p = position.toLowerCase();
  return p === 'center' || p === 'middle';
}

export function standaloneToast(options: LegacyToastOptions): void {
  const { status, position, isClosable, ...rest } = options;
  const type = mapStatusToType(status);
  const useCenter = isCenterPosition(position);
  const target = useCenter ? centeredToaster : toaster;
  const placement = useCenter
    ? undefined
    : position
      ? (positionMap[position] ?? 'top-end')
      : undefined;
  target.create({
    ...rest,
    type,
    placement,
    meta: {
      closable: isClosable !== false,
    },
  });
}

function toastTypeKey(type: unknown): string {
  return String(type ?? 'info');
}

function toastBorderColor(typeKey: string): string {
  if (typeKey === 'success') {
    return 'success';
  }
  if (typeKey === 'error') {
    return 'error';
  }
  if (typeKey === 'warning') {
    return 'warning';
  }
  return 'body2';
}

function toastDescriptionColor(typeKey: string): string {
  if (typeKey === 'success') {
    return 'success';
  }
  if (typeKey === 'error') {
    return 'lightError';
  }
  if (typeKey === 'warning') {
    return 'warning';
  }
  return 'secondary2';
}

function toastTitleColor(typeKey: string): string {
  if (typeKey === 'success') {
    return 'success';
  }
  if (typeKey === 'error') {
    return 'error';
  }
  if (typeKey === 'warning') {
    return 'warning';
  }
  return 'body';
}

function ToastItem({
  t,
}: {
  t: ToastOptions<React.ReactNode>;
}): React.ReactElement {
  const typeKey = toastTypeKey(t.type);
  const darkPanel =
    typeKey === 'info' ||
    typeKey === 'loading' ||
    typeKey === 'success' ||
    typeKey === 'error' ||
    typeKey === 'warning';
  const borderColor = toastBorderColor(typeKey);
  const descriptionColor = toastDescriptionColor(typeKey);
  return (
    <Toast.Root
      bg={darkPanel ? 'dark' : undefined}
      color={darkPanel ? 'body' : undefined}
      borderWidth={darkPanel ? '1px' : undefined}
      borderColor={darkPanel ? borderColor : undefined}
      minW="min(100%, 20rem)"
      maxW="md"
      w="100%"
      shadow="lg"
    >
      {t.title != null && t.title !== '' ? (
        <Toast.Title color={darkPanel ? toastTitleColor(typeKey) : undefined}>
          {t.title}
        </Toast.Title>
      ) : null}
      {t.description != null && t.description !== '' ? (
        <Toast.Description
          color={darkPanel ? descriptionColor : undefined}
          display="block"
          whiteSpace="normal"
          wordBreak="break-word"
        >
          {t.description}
        </Toast.Description>
      ) : null}
      {t.meta?.closable !== false ? (
        <Toast.CloseTrigger color={darkPanel ? 'body' : undefined} />
      ) : null}
    </Toast.Root>
  );
}

const toasterLayoutProps = {
  gap: 3,
  maxW: 'md',
  w: { base: 'calc(100vw - 2rem)', md: 'md' },
} as const;

export const ToastProvider: React.FC = () => (
  <>
    <Toaster toaster={toaster} {...toasterLayoutProps}>
      {(t: ToastOptions<React.ReactNode>) => (
        <React.Fragment key={t.id}>
          <ToastItem t={t} />
        </React.Fragment>
      )}
    </Toaster>
    <Toaster
      toaster={centeredToaster}
      position="fixed"
      inset={0}
      zIndex={1600}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={3}
      pointerEvents="none"
      maxW="100vw"
      w="100vw"
    >
      {(t: ToastOptions<React.ReactNode>) => (
        <Box
          key={t.id}
          pointerEvents="auto"
          w="100%"
          maxW="md"
          display="flex"
          justifyContent="center"
        >
          <ToastItem t={t} />
        </Box>
      )}
    </Toaster>
  </>
);

export const ToastContainer = ToastProvider;
